import type { AppProps } from 'next/app';
import React, { ReactElement, ReactNode, createContext, useContext, useEffect } from 'react';
import { NextPage } from 'next';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/pagination';
import '@styles/global.scss';
import ErrorBoundary from '@components/error/ErrorBoundary';
import { enableStaticRendering, observer } from 'mobx-react-lite';
import { isServer, showLoading } from '@src/utils/util';
import { appWithTranslation } from 'next-i18next';
import nextI18NextConfig from 'next-i18next.config.js';
import { SWRConfig } from 'swr';
import { fetcher } from '@src/utils/fetcher';
import { GlobalStoreProvider } from '@src/hooks/use-global-store';
import { AppRoot } from '@components/AppRoot';
import '../utils/polyfills';
import { AdobeDataLayerProvider } from '@src/adobe-launch/data-layer-provider';
import { TrackerProvider } from '@src/hooks/tracker/use-page-trackers';
import 'antd-mobile/bundle/css-vars-patch.css';
import 'video.js/dist/video-js.css';
import loadingStore from '@mobx/stores/loading-store';
import LoadingST from '@components/loading-ST/LoadingST';

export type NextPageWithLayout = NextPage & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

enableStaticRendering(isServer);

const StoreContext = createContext({ loadingStore });

export const useStores = () => useContext(StoreContext);

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
    useEffect(() => {
        showLoading();
    }, []);

    return (
        <StoreContext.Provider value={{ loadingStore }}>
            <div className={`text-base ${pageProps?._nextI18Next?.initialLocale || 'en-US'}`}>
                <ErrorBoundary>
                    <GlobalStoreProvider>
                        <AdobeDataLayerProvider>
                            <TrackerProvider>
                                <SWRConfig
                                    value={{
                                        revalidateIfStale: false,
                                        revalidateOnFocus: false,
                                        fetcher: fetcher,
                                    }}
                                >
                                    <LoadingST />
                                    <AppRoot page={Component} pageProps={pageProps} />
                                </SWRConfig>
                            </TrackerProvider>
                        </AdobeDataLayerProvider>
                    </GlobalStoreProvider>
                </ErrorBoundary>
            </div>
        </StoreContext.Provider>
    );
}

export default appWithTranslation(observer(MyApp), nextI18NextConfig);
